import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Share, SquarePlus } from 'lucide-react';
import { errorMessage } from '../../services/errors';
import type { PushStatus } from '../../services/createWebPush';
import { sendTestNotification, webPush } from '../../services/webPush';
import { useRefreshWebPushStatus, useWebPushStatus } from '../../services/webPushStatus';
import { outlineButton, primaryButton } from '../../components/styles';

/**
 * Notifications on this device (web push): the site on an iPhone's Home
 * Screen or in a desktop browser receives digests and reminders, like the
 * Android app. Self-contained: it reads nothing from the profile.
 */
export default function NotificationsSetting() {
  const status = useWebPushStatus();
  const refresh = useRefreshWebPushStatus();
  const turnOn = useMutation({ mutationFn: () => webPush.turnOn(), onSettled: refresh });
  const turnOff = useMutation({ mutationFn: () => webPush.turnOff(), onSettled: refresh });
  const sendTest = useMutation({ mutationFn: sendTestNotification });
  const busy = turnOn.isPending || turnOff.isPending;
  const [syncError, setSyncError] = useState<unknown>(null);

  // A device that is on registers itself again on each visit: its last-seen
  // time is refreshed, a subscription the push service replaced moves over,
  // and one made with a key the server no longer holds is made again.
  // A refusal is shown: the device would otherwise look on while nothing reaches it.
  useEffect(() => {
    if (status.data !== 'on') return;
    let current = true;
    webPush.sync().then(
      () => current && setSyncError(null),
      (error: unknown) => current && setSyncError(error),
    );
    return () => {
      current = false;
    };
  }, [status.data]);

  if (status.isPending) return <p className="text-sm text-on-surface-variant">Checking this device…</p>;
  if (status.isError) return <p className="text-sm text-error">Could not check this device: {errorMessage(status.error)}</p>;

  const state: PushStatus = status.data;
  return (
    <div className="space-y-3">
      <p className="text-sm text-on-surface-variant">
        Your daily digest and task reminders reach this device as notifications, the way they reach the Android app. Each device is turned
        on separately.
      </p>
      {state === 'needs-home-screen' && <HomeScreenSteps />}
      {state === 'unsupported' && <p className="text-sm">This browser cannot receive notifications. On an iPhone, use the site from the Home Screen; on a computer, use Chrome, Edge, Firefox or Safari.</p>}
      {state === 'blocked' && (
        <p className="text-sm text-error">
          Notifications are blocked for cerebrodiem.com in this browser's settings. Allow them there, then turn them on here.
        </p>
      )}
      {state === 'off' && (
        <button type="button" onClick={() => turnOn.mutate()} disabled={busy} className={primaryButton}>
          {turnOn.isPending ? 'Turning on…' : 'Turn on notifications on this device'}
        </button>
      )}
      {state === 'on' && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-success">This device receives your notifications.</p>
          <button type="button" onClick={() => sendTest.mutate()} disabled={busy || sendTest.isPending} className={outlineButton}>
            {sendTest.isPending ? 'Sending…' : 'Send a test'}
          </button>
          <button type="button" onClick={() => turnOff.mutate()} disabled={busy} className={outlineButton}>
            {turnOff.isPending ? 'Turning off…' : 'Turn off on this device'}
          </button>
        </div>
      )}
      {state === 'on' && sendTest.isSuccess && (
        <p className="text-sm text-on-surface-variant">
          Sent to {sendTest.data} {sendTest.data === 1 ? 'device' : 'devices'}. It arrives within a minute.
        </p>
      )}
      {state === 'on' && sendTest.isError && <p className="text-sm text-error">Not sent: {errorMessage(sendTest.error)}</p>}
      {turnOn.isError && <p className="text-sm text-error">Not turned on: {errorMessage(turnOn.error)}</p>}
      {/* Turning off does everything it can before reporting: the device is off (nothing reaches a dropped subscription) unless the status above still says on. */}
      {turnOff.isError && <p className="text-sm text-error">Not turned off cleanly: {errorMessage(turnOff.error)}</p>}
      {syncError !== null && state === 'on' && (
        <p className="text-sm text-error">This device could not be registered again: {errorMessage(syncError)}. Turn notifications off and on to retry.</p>
      )}
    </div>
  );
}

/** iPhone and iPad: push exists only for a site added to the Home Screen and opened from there (iOS 16.4 or later). */
function HomeScreenSteps() {
  return (
    <div className="rounded-xl bg-surface-variant/30 p-4 space-y-2 text-sm">
      <p className="font-medium">On an iPhone, notifications need the site on your Home Screen first:</p>
      <ol className="list-decimal pl-5 space-y-1">
        <li>
          In Safari, tap Share <Share className="inline w-4 h-4 align-text-bottom" aria-label="Share" /> and choose Add to Home Screen{' '}
          <SquarePlus className="inline w-4 h-4 align-text-bottom" aria-label="Add to Home Screen" />.
        </li>
        <li>Open Cerebro Diem from the Home Screen icon, not from Safari.</li>
        <li>Come back to Settings there and turn notifications on.</li>
      </ol>
      <p className="text-on-surface-variant">Needs iOS 16.4 or later.</p>
    </div>
  );
}
